/**
 * Utilitaires pour la gestion des dates dans SessionForm
 */

/**
 * Formater une date pour les inputs HTML de type date
 * @param {string|Date} dateString - Date à formater
 * @returns {string} Date au format YYYY-MM-DD
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Si la date contient un T (format ISO avec heure)
    if (typeof dateString === 'string' && dateString.includes('T')) {
      const utcDate = new Date(dateString);
      const year = utcDate.getUTCFullYear();
      const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // Si déjà au format YYYY-MM-DD
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }
    
    // Conversion générique
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn('Date invalide:', dateString);
      return '';
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
    
  } catch (error) {
    console.error('Erreur formatage date:', error, dateString);
    return '';
  }
};

/**
 * Convertir une date vers UTC pour l'API
 * AMÉLIORÉ: Accepte maintenant les objets Date ET les strings
 * @param {string|Date} dateInput - Date au format YYYY-MM-DD ou objet Date
 * @returns {string} Date au format ISO UTC
 */
export const convertFormDateToUTC = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    // Si c'est un objet Date, le convertir directement
    if (dateInput instanceof Date) {
      const year = dateInput.getFullYear();
      const month = String(dateInput.getMonth() + 1).padStart(2, '0');
      const day = String(dateInput.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}T12:00:00.000Z`;
    }
    
    // Si c'est une string
    const dateString = dateInput.toString();
    
    // Si déjà au format ISO, retourner tel quel
    if (dateString.includes('T')) {
      return dateString;
    }
    
    // Si au format YYYY-MM-DD, ajouter l'heure par défaut
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString + 'T12:00:00.000Z';
    }
    
    return dateString;
  } catch (error) {
    console.error('Erreur conversion date UTC:', error, dateInput);
    return typeof dateInput === 'string' ? dateInput : '';
  }
};

/**
 * NOUVELLE: Convertir une date UTC vers format local YYYY-MM-DD
 * @param {string} utcDateString - Date au format ISO UTC ou YYYY-MM-DD
 * @returns {string|null} Date au format YYYY-MM-DD ou null si invalide
 */
export const convertUTCToLocalDate = (utcDateString) => {
  if (!utcDateString) return null;
  
  try {
    // Si contient T (format ISO avec heure)
    if (utcDateString.includes('T')) {
      const utcDate = new Date(utcDateString);
      if (isNaN(utcDate.getTime())) return null;
      
      const year = utcDate.getUTCFullYear();
      const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // Si déjà au format YYYY-MM-DD, retourner tel quel
    if (utcDateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return utcDateString;
    }
    
    // Autres formats, tentative de conversion
    const date = new Date(utcDateString);
    if (isNaN(date.getTime())) return null;
    
    return formatDateForInput(date);
    
  } catch (error) {
    console.error('Erreur conversion UTC vers local:', error, utcDateString);
    return null;
  }
};

/**
 * Vérifier si deux dates sont le même jour
 * @param {string|Date} date1 - Première date
 * @param {string|Date} date2 - Deuxième date
 * @returns {boolean} True si même jour
 */
export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;
  
  try {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  } catch (error) {
    console.error('Erreur comparaison dates:', error);
    return false;
  }
};

/**
 * Calculer le nombre de jours entre deux dates
 * @param {string|Date} startDate - Date de début
 * @param {string|Date} endDate - Date de fin
 * @returns {number} Nombre de jours
 */
export const getDaysBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } catch (error) {
    console.error('Erreur calcul jours:', error);
    return 0;
  }
};

/**
 * Calculer les jours ouvrés entre deux dates (lundi à vendredi)
 * @param {string|Date} startDate - Date de début
 * @param {string|Date} endDate - Date de fin
 * @param {boolean} includeWeekends - Inclure les weekends (défaut: false)
 * @returns {Array} Tableau des jours ouvrés
 */
export const getWorkingDaysBetween = (startDate, endDate, includeWeekends = false) => {
  if (!startDate || !endDate) return [];
  
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = [];
    const currentDate = new Date(start);
    
    while (currentDate <= end) {
      const dayOfWeek = currentDate.getDay();
      
      // Inclure selon les paramètres
      const shouldInclude = includeWeekends || (dayOfWeek >= 1 && dayOfWeek <= 5);
      
      if (shouldInclude) {
        const dateStr = toLocalDateStr(currentDate);
        const dayNumber = days.length + 1;
        const dayLabel = `Jour ${dayNumber} - ${currentDate.toLocaleDateString('fr-FR', { 
          weekday: 'short',
          day: '2-digit',
          month: '2-digit'
        })}`;
        
        days.push({
          date: dateStr,
          label: dayLabel,
          dayNumber,
          dayOfWeek,
          isWeekend: dayOfWeek === 0 || dayOfWeek === 6
        });
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  } catch (error) {
    console.error('Erreur calcul jours ouvrés:', error);
    return [];
  }
};

/**
 * Formater une date pour l'affichage (format français)
 * @param {string|Date} dateString - Date à formater
 * @param {object} options - Options de formatage
 * @returns {string} Date formatée
 */
export const formatDateForDisplay = (dateString, options = {}) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    
    const defaultOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...options
    };
    
    return date.toLocaleDateString('fr-FR', defaultOptions);
  } catch (error) {
    console.error('Erreur formatage date affichage:', error);
    return '';
  }
};

/**
 * Formater une date courte pour l'affichage
 * @param {string|Date} dateString - Date à formater
 * @returns {string} Date formatée (jj/mm/aaaa)
 */
export const formatShortDate = (dateString) => {
  return formatDateForDisplay(dateString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Obtenir la date d'aujourd'hui au format YYYY-MM-DD
 * @returns {string} Date d'aujourd'hui
 */
export const getTodayDateString = () => {
  try {
    const today = new Date();
    return formatDateForInput(today);
  } catch (error) {
    console.error('Erreur date aujourd\'hui:', error);
    return '';
  }
};

/**
 * Ajouter des jours à une date
 * @param {string|Date} dateString - Date de base
 * @param {number} days - Nombre de jours à ajouter
 * @returns {string} Nouvelle date au format YYYY-MM-DD
 */
export const addDays = (dateString, days) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    date.setDate(date.getDate() + days);
    return formatDateForInput(date);
  } catch (error) {
    console.error('Erreur ajout jours:', error);
    return '';
  }
};

/**
 * Vérifier si une date est dans le futur
 * @param {string|Date} dateString - Date à vérifier
 * @returns {boolean} True si dans le futur
 */
export const isFutureDate = (dateString) => {
  if (!dateString) return false;
  
  try {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    
    return date > today;
  } catch (error) {
    console.error('Erreur vérification date future:', error);
    return false;
  }
};

/**
 * Vérifier si une date est dans le passé
 * @param {string|Date} dateString - Date à vérifier
 * @returns {boolean} True si dans le passé
 */
export const isPastDate = (dateString) => {
  if (!dateString) return false;
  
  try {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    
    return date < today;
  } catch (error) {
    console.error('Erreur vérification date passée:', error);
    return false;
  }
};

/**
 * Valider une période de dates
 * @param {string} startDate - Date de début
 * @param {string} endDate - Date de fin
 * @returns {object} Résultat de validation
 */
export const validateDateRange = (startDate, endDate) => {
  const errors = [];
  
  if (!startDate) {
    errors.push('Date de début requise');
  }
  
  if (!endDate) {
    errors.push('Date de fin requise');
  }
  
  if (startDate && endDate) {
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      if (isNaN(start.getTime())) {
        errors.push('Date de début invalide');
      }
      
      if (isNaN(end.getTime())) {
        errors.push('Date de fin invalide');
      }
      
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start > end) {
        errors.push('La date de début doit être antérieure à la date de fin');
      }
    } catch (error) {
      errors.push('Format de date invalide');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculer la durée en jours d'une formation
 * @param {string} startDate - Date de début
 * @param {string} endDate - Date de fin
 * @param {boolean} workingDaysOnly - Compter seulement les jours ouvrés
 * @returns {number} Durée en jours
 */
export const getTrainingDuration = (startDate, endDate, workingDaysOnly = true) => {
  if (!startDate || !endDate) return 0;
  
  if (isSameDay(startDate, endDate)) {
    return 1;
  }
  
  if (workingDaysOnly) {
    const workingDays = getWorkingDaysBetween(startDate, endDate);
    return workingDays.length;
  } else {
    return getDaysBetween(startDate, endDate) + 1;
  }
};

/**
 * Formater une date/heure pour l'affichage (format français court)
 * @param {string|Date} dateString - Date à formater (ISO ou Date)
 * @param {object} options - Options de formatage
 * @returns {string} Date/heure formatée (ex: "01/01 à 18:12")
 */
export const formatDateTime = (dateString, options = {}) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const defaultOptions = {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    };

    return date.toLocaleString('fr-FR', defaultOptions);
  } catch (error) {
    console.error('Erreur formatage date/heure:', error);
    return '';
  }
};

/**
 * Convertir un objet Date en string YYYY-MM-DD en timezone locale
 * Remplacement safe de `date.toISOString().split('T')[0]` (qui utilise UTC)
 * @param {Date} date - Objet Date
 * @returns {string} Date au format YYYY-MM-DD en timezone locale
 */
export const toLocalDateStr = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export default {
  formatDateForInput,
  toLocalDateStr,
  convertFormDateToUTC,
  convertUTCToLocalDate,
  isSameDay,
  getDaysBetween,
  getWorkingDaysBetween,
  formatDateForDisplay,
  formatShortDate,
  formatDateTime,
  getTodayDateString,
  addDays,
  isFutureDate,
  isPastDate,
  validateDateRange,
  getTrainingDuration
};